import useCurrentView from '@/composables/saved-views/current-view';
import useDefaultView from '@/composables/saved-views/default-view';
import useEnrichParams from '@/composables/saved-views/enrich-params';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useSelectedViews from '@/composables/saved-views/selected-views';
import useViewAccess from '@/composables/saved-views/view-access';
import useViewCollections from '@/composables/saved-views/view-collections';
import useViews from '@/composables/saved-views/views';
import useViewsContext from '@/composables/saved-views/views-context';
import useViewsRepo from '@/composables/saved-views/views-repo';
import useViewsRepoLocal from '@/composables/saved-views/views-repo-local';
import { computed } from 'vue';
import { findViews } from '@/lib/saved-view/saved-view';

export default function useInMemoryViews({
  defaultProps,
  defaultView,
  application,
  space = null,
  planning = null,
  gridPageTile = null,
  user = null,
  readOnly,
  includeGoalCycle = false,
}) {
  const selectedViewsSvc = useSelectedViews();
  const { loggedInUserAccount } = useLoggedInUserAccount();
  const { loggedInUser } = useLoggedInUser();
  const context = useViewsContext(application, readOnly);

  const viewCollectionSvc = useViewCollections();
  const viewCollection = computed(() => {
    const viewCollections = findViews({
      views: viewCollectionSvc.viewCollections.value,
      application,
      space,
      planning,
      user,
      gridPageTile,
    });
    if (viewCollections.length === 0) {
      return null;
    }
    return viewCollections[0];
  });

  const viewsRepo = useViewsRepo({
    application,
    space,
    planning,
    gridPageTile,
  });
  const defaultViewRepo = useDefaultView(
    defaultView,
    defaultProps,
    application,
    space,
    planning,
    user,
    loggedInUser,
    loggedInUserAccount,
    gridPageTile,
    viewsRepo,
    viewCollection,
  );
  const enrichParamsRepo = useEnrichParams(defaultViewRepo);
  const viewsRepoLocal = useViewsRepoLocal(enrichParamsRepo, loggedInUserAccount);
  const repo = useViewAccess(viewsRepoLocal, context);

  const selectedViewForApplication = selectedViewsSvc.getSelectedViewForApplication({
    application,
    space,
    planning,
    user,
    gridPageTile,
  });

  const changeSelectedView = (view) => selectedViewsSvc.saveOrUpdateSelectedView(selectedViewForApplication.value, view.uid);

  const viewsSvc = useViews({
    application,
    space,
    planning,
    user,
    gridPageTile,
    selectedViewsSvc,
    changeSelectedView,
    repo,
    viewCollection,
  });

  const currentViewSvc = useCurrentView(
    defaultProps,
    viewsSvc,
    selectedViewForApplication,
  );

  const createViews = (views, viewOrder) => {
    if (includeGoalCycle) {
      return viewsSvc.createViews(views.map((v) => ({
        ...v,
        goalCycles: currentViewSvc.currentView.value.goalCycles,
      })), viewOrder);
    }
    return viewsSvc.createViews(views, viewOrder);
  };

  return {
    createLoading: viewsSvc.createLoading,
    createViews,

    loadingUpdateView: viewsSvc.loadingUpdateView,
    updateView: viewsSvc.updateView,

    loadingDeleteView: viewsSvc.loadingDeleteView,
    deleteView: viewsSvc.deleteView,

    setParams: viewsSvc.setParams,
    updateSort: viewsSvc.updateSort,
    updateCycles: viewsSvc.updateCycles,
    updateLockCyclesOnView: viewsSvc.updateLockCyclesOnView,

    currentViewHasUnsavedChanges: currentViewSvc.hasUnsavedChanges,
    currentViewUnsavedChanges: currentViewSvc.unsavedChanges,

    resetView: viewsSvc.resetView,

    views: viewsSvc.views,
    viewCollection: viewsSvc.viewCollection,

    currentView: currentViewSvc.currentView,
    defaultView,
    context,

    changeSelectedView,

    initCurrentView: viewsSvc.initCurrentView,
  };
}
